import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import format from 'string-template';

import useComponentMounted from '../../../hooks/useComponentMounted';
import useStorage, { StorageProcessState } from '../../../hooks/useStorage';
import { storagePaths, pathPlaceholder } from '../../../utils/firebasePaths';
import LoadingPage from '../../../components/LoadingPage';
import Form, { FormType } from '../../Model/Form';
import CoachesListContext from '../../context/CoachesListContext';
import useToolsNavigation from '../../hooks/useToolsNavigation';
import useToast from '../../hooks/useToast';
import AutoComplete from '../AutoComplete';
import LoggedInUserContext from '../../../context/LoggedInUserContext';

import FormQuestions from './components/FormQuestions';
import HiddenFields from './components/HiddenFields';
import EndScreen from './components/EndScreen/EndScreen';
import { initialValues as startValues } from './validation';
import fieldName, {
  endScreenContentFieldName,
  MIN_SELECTION_LIMIT,
  questionFieldName,
  questionId,
} from './formFields';
import TextBlastConsent from './components/TextBlastConsent';

import {
  Container,
  HeaderContainer,
  Title,
  StyledBackButton,
  StyledForm,
  FormSectionContainer,
  StyledFormInput,
  StyledFormButton,
  StyledTopRow,
  StyledBottomRow,
  StyledTypography,
  CoachContainer,
} from './styles';
import texts from './texts';
import QuestionType from './questionType';

const ESCAPED_COMMA_PLACEHOLDER = '___ESCAPED_COMMA___';

const parseOptions = (options = '') => (
  options
    .replace(/\\,/g, ESCAPED_COMMA_PLACEHOLDER)
    .split(',')
    .map((option) => option.replace(new RegExp(ESCAPED_COMMA_PLACEHOLDER, 'g'), ',').trim())
    .filter(Boolean)
);

const FormBuilder = ({
  formDoc,
  onBackClick,
  type,
}) => {
  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();
  const { navigateToForm } = useToolsNavigation();
  const { uploadJSON, getJSONData } = useStorage();

  const { coachesCollection } = useContext(CoachesListContext);

  const isEditView = !!formDoc;
  const formType = isEditView ? formDoc.type : type;

  const [initialFormValues, setInitialFormValues] = useState(startValues(formType));

  const {
    userId: createdBy,
  } = useContext(LoggedInUserContext);

  const coaches = useMemo(() => (
    coachesCollection.docs.map(({ id, name }) => ({ id, label: name }))
  ), [coachesCollection]);

  const parseSelectionLimit = useCallback((value) => {
    if (value === '' || value === null || typeof value === 'undefined') {
      return { value: undefined };
    }

    const parsedValue = Number(value);

    if (!Number.isInteger(parsedValue) || parsedValue < MIN_SELECTION_LIMIT) {
      return { error: texts.validation.selectionLimits.invalidValue };
    }

    return { value: parsedValue };
  }, []);

  const handleSubmit = useCallback(async (values, actions) => {
    let errMsg;

    const {
      [fieldName.NAME]: formName,
      [fieldName.COACH]: coach,
      [fieldName.COACH_ID]: coachId,
      [fieldName.FIELDS]: questions,
      [fieldName.HIDDEN_FIELDS]: hiddenFields,
      [fieldName.END_SCREEN_CONTENT]: {
        [endScreenContentFieldName.LINE_1]: endScreenLine1,
        [endScreenContentFieldName.LINE_2]: endScreenLine2,
      } = {},
      [fieldName.TEXT_BLAST_CONSENT]: shouldShowTextBlastConsent,
    } = values;
    const setSelectionLimitError = (fieldPath, errorMessage) => {
      actions.setFieldTouched(fieldPath, true, false);
      actions.setFieldError(fieldPath, errorMessage);
      if (!errMsg) {
        errMsg = errorMessage;
      }
    };

    questions.forEach((question, index) => {
      if (question.type !== QuestionType.OPTIONS) {
        return;
      }

      actions.setFieldError(
        `${fieldName.FIELDS}.${index}.${questionFieldName.MIN_SELECTIONS}`,
        undefined,
      );
      actions.setFieldError(
        `${fieldName.FIELDS}.${index}.${questionFieldName.MAX_SELECTIONS}`,
        undefined,
      );
    });

    // Remove isDefault attributes from the from questions as they are not needed for the form after creation
    const updatedQuestions = questions.map((question, index) => {
      const { isDefault, ...rest } = question;
      if (question.type !== QuestionType.OPTIONS) {
        return rest;
      }

      const updatedQuestion = { ...rest };
      const shouldValidateSelectionLimits = formType === FormType.POST_PAYMENT && question.isMultiple;
      const minSelectionsFieldPath = `${fieldName.FIELDS}.${index}.${questionFieldName.MIN_SELECTIONS}`;
      const maxSelectionsFieldPath = `${fieldName.FIELDS}.${index}.${questionFieldName.MAX_SELECTIONS}`;
      const {
        value: minSelections,
        error: minSelectionError,
      } = parseSelectionLimit(question[questionFieldName.MIN_SELECTIONS]);
      const {
        value: maxSelections,
        error: maxSelectionError,
      } = parseSelectionLimit(question[questionFieldName.MAX_SELECTIONS]);

      if (shouldValidateSelectionLimits) {
        const options = parseOptions(question.options);
        if (minSelectionError) {
          setSelectionLimitError(minSelectionsFieldPath, minSelectionError);
        }

        if (maxSelectionError) {
          setSelectionLimitError(maxSelectionsFieldPath, maxSelectionError);
        }

        if (
          typeof minSelections !== 'undefined'
          && typeof maxSelections !== 'undefined'
          && minSelections > maxSelections
        ) {
          setSelectionLimitError(minSelectionsFieldPath, texts.validation.selectionLimits.invalidRange);
          setSelectionLimitError(maxSelectionsFieldPath, texts.validation.selectionLimits.invalidRange);
        } else if (
          typeof minSelections !== 'undefined'
          && minSelections > options.length
        ) {
          setSelectionLimitError(
            minSelectionsFieldPath,
            texts.validation.selectionLimits.minimumExceedsOptions,
          );
        }
      }

      if (shouldValidateSelectionLimits && typeof minSelections !== 'undefined') {
        updatedQuestion[questionFieldName.MIN_SELECTIONS] = minSelections;
      } else {
        delete updatedQuestion[questionFieldName.MIN_SELECTIONS];
      }

      if (shouldValidateSelectionLimits && typeof maxSelections !== 'undefined') {
        updatedQuestion[questionFieldName.MAX_SELECTIONS] = maxSelections;
      } else {
        delete updatedQuestion[questionFieldName.MAX_SELECTIONS];
      }

      return updatedQuestion;
    });
    const updatedHiddenFields = hiddenFields?.map((hiddenField) => {
      const { isDefault, ...rest } = hiddenField;
      return rest;
    });

    if (!coach) { // First, check if a coach is selected
      errMsg = texts.validation.coach;
    } else if (!formName) { // The form name should not be empty either
      errMsg = texts.validation.formNameRequired;
    } else if (updatedQuestions.length === 0) { // Then, check if there are at least one question in the form
      errMsg = texts.validation.questionsRequired;
    } else if (formType === FormType.LEAD && (!endScreenLine1 || !endScreenLine2)) {
      // Also check whether the end screen values are not empty for lead forms
      errMsg = texts.validation.endScreenContentRequired;
    }

    // Check the financial commitment question
    const financialCommitment = updatedQuestions.find(
      (question) => question.id === questionId.FINANCIAL_COMMITMENT,
    );
    if (financialCommitment) {
      if (financialCommitment.type === QuestionType.OPTIONS) {
        const options = financialCommitment.options.split(',').map((option) => option.trim().toLowerCase());
        // Check for at least 2 options
        if (options.length < 2) {
          errMsg = texts.validation.financialCommitment.options;
        } else {
          // Check if one option starts with "Yes" and another starts with "No"
          const hasYes = options.some((item) => item.startsWith(texts.yes));
          const hasNo = options.some((item) => item.startsWith(texts.no));
          if (!hasYes || !hasNo) {
            errMsg = texts.validation.financialCommitment.requiredYesAndNo;
          }
        }
      } else {
        errMsg = texts.validation.financialCommitment.type;
      }
    }

    // Check if the prompt input is one of the options for all multiple selection questions
    const multipleSelectionQuestions = updatedQuestions.filter(
      (question) => question.type === QuestionType.OPTIONS,
    );
    multipleSelectionQuestions.forEach((question) => {
      const options = question.options.split(',').map((option) => option.trim().toLowerCase());
      const promptInput = question.promptInput?.trim()?.toLowerCase();
      if (!!promptInput && !options.includes(promptInput)) {
        errMsg = texts.validation.promptInputNotInOptions;
      }
    });

    if (errMsg) {
      showToast(errMsg, { error: true });
      actions.setSubmitting(false);
      return;
    }

    // After form validation, we will save the form in firestore
    const newFormData = {
      formName,
      coach,
      coachId,
    };

    // Then, the form will be saved in storage for public access using the form ID
    const storagePath = format(storagePaths.COACH_FORM_COLLECTION, {
      [pathPlaceholder.COACH_ID]: coachId,
    });

    if (isEditView) {
      const formId = formDoc.id;
      await formDoc.updateFields({ ...newFormData });

      // Preserve any existing IS config fields (e.g. disqualifiedCountries, directPurchaseLink)
      // that are managed by other components and stored alongside the form config
      const existingFormData = await getJSONData(`${storagePath}/${formId}`);

      const formConfigString = JSON.stringify({
        ...existingFormData,
        ...values,
        [fieldName.FIELDS]: updatedQuestions,
        // Only add hidden fields to lead forms
        ...(formType === FormType.LEAD && {
          [fieldName.HIDDEN_FIELDS]: updatedHiddenFields,
          shouldShowTextBlastConsent,
        }),
      });
      await uploadJSON(formConfigString, storagePath, formId);

      showToast(texts.formPublished);
      return;
    }

    // Reserve the doc id and upload the storage JSON to it BEFORE the Firestore doc exists,
    // then create the doc last. Mirrors FormSettings.jsx's create flow: a server-side trigger
    // reacting to the doc's existence must never see it pointing at not-yet-written content,
    // and a failed upload must never leave behind a doc at all.
    const formId = Form.reserveFormId();
    const formConfigString = JSON.stringify({
      ...values,
      [fieldName.FIELDS]: updatedQuestions,
      // Only add hidden fields to lead forms
      ...(formType === FormType.LEAD && {
        [fieldName.HIDDEN_FIELDS]: updatedHiddenFields,
        shouldShowTextBlastConsent,
      }),
    });

    const uploadResult = await uploadJSON(formConfigString, storagePath, formId);
    if (uploadResult?.state !== StorageProcessState.SUCCESS) {
      showToast(texts.uploadError, { error: true });
      actions.setSubmitting(false);
      return;
    }

    await Form.addFormWithId(formId, {
      ...newFormData,
      createdBy,
      type: formType,
      isArchived: false,
    });

    showToast(texts.formPublished);

    // Load the newly created form in the editor
    navigateToForm(formId);
  }, [
    uploadJSON,
    getJSONData,
    showToast,
    navigateToForm,
    formDoc,
    isEditView,
    createdBy,
    formType,
    parseSelectionLimit,
  ]);

  useEffect(() => {
    const fetchFormData = async () => {
      const {
        id: formId,
        coach,
        coachId,
        formName,
      } = formDoc;

      // Fetch form data from the storage JSON file
      const storagePath = format(storagePaths.COACH_FORM_COLLECTION, {
        [pathPlaceholder.COACH_ID]: coachId,
      });
      const formData = await getJSONData(`${storagePath}/${formId}`);
      const {
        fields,
        hiddenFields,
        endScreenContent,
        shouldShowTextBlastConsent,
      } = formData;

      const values = {
        [fieldName.COACH]: coach,
        [fieldName.COACH_ID]: coachId,
        [fieldName.NAME]: formName,
        [fieldName.FIELDS]: fields,
        // Fields exclusive to lead forms
        ...(formType === FormType.LEAD && {
          [fieldName.HIDDEN_FIELDS]: hiddenFields,
          [fieldName.END_SCREEN_CONTENT]: endScreenContent,
        }),
        [fieldName.TEXT_BLAST_CONSENT]: shouldShowTextBlastConsent,
      };

      if (isComponentMountedRef.current) {
        setInitialFormValues(values);
      }
    };

    if (isEditView) {
      fetchFormData();
    }
  }, [
    formDoc,
    isEditView,
    getJSONData,
    isComponentMountedRef,
    formType,
  ]);

  // Show loading page until form data is loaded from storage
  if (isEditView && initialFormValues === startValues) {
    return <LoadingPage />;
  }

  return (
    <Container>
      <HeaderContainer>
        <Title>
          {isEditView ? texts.editForm : texts.createForm}
        </Title>
        <StyledBackButton
          onClick={onBackClick}
        >
          {texts.back}
        </StyledBackButton>
      </HeaderContainer>
      <Formik
        initialValues={initialFormValues}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          setFieldValue,
          isSubmitting,
        }) => (
          <StyledForm>
            <StyledTopRow>
              <FormSectionContainer>
                <StyledTypography>
                  {texts.selectDescription}
                  {isEditView && ` : ${formDoc[fieldName.COACH]}`}
                </StyledTypography>
                {!isEditView
                && (
                  <CoachContainer>
                    <AutoComplete
                      options={coaches}
                      onChange={(data) => {
                        setFieldValue(fieldName.COACH_ID, data?.id);
                        setFieldValue(fieldName.COACH, data?.label);
                      }}
                    />
                  </CoachContainer>
                )}
              </FormSectionContainer>
            </StyledTopRow>
            <StyledFormInput
              name={fieldName.NAME}
              label={texts.field[fieldName.NAME].label}
            />
            {formType === FormType.LEAD && (
              <HiddenFields hiddenFields={values[fieldName.HIDDEN_FIELDS]} />
            )}
            <FormQuestions questions={values[fieldName.FIELDS]} formType={formType} />
            {formType === FormType.LEAD && (
              <>
                <EndScreen />
                <TextBlastConsent />
              </>
            )}
            <StyledBottomRow>
              <StyledFormButton
                disabled={isSubmitting}
                type="submit"
              >
                {texts.submitButton}
              </StyledFormButton>
            </StyledBottomRow>
          </StyledForm>
        )}
      </Formik>
    </Container>
  );
};

FormBuilder.propTypes = {
  formDoc: PropTypes.object,
  onBackClick: PropTypes.func.isRequired,
  type: PropTypes.string,
};

FormBuilder.defaultProps = {
  formDoc: null,
  type: null,
};

export default FormBuilder;
